import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import Layout from '../../common/components/Navbar';
import { notifications } from '@mantine/notifications';
import {
  Title,
  Group,
  Button,
  Paper,
  Table,
  Badge,
  Select,
  TextInput,
  Modal,
  Text,
  ScrollArea,
  Tabs,
  Box,
  Grid,
  Alert,
  Collapse,
  Image,
  ActionIcon
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import {
  IconSearch,
  IconFilter,
  IconEye,
  IconCheck,
  IconArchive,
  IconAlertTriangle,
  IconMessageExclamation,
  IconChevronDown,
  IconX
} from '@tabler/icons-react';

const Complaints = () => {
  // Data
  const [complaints, setComplaints] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  
  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);

  // Incident Alerts
  const [incidentCount, setIncidentCount] = useState(0);
  const [showIncidentDetails, setShowIncidentDetails] = useState(false);

  // Modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState('');

  // Image Preview State
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    fetchComplaints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateRange, typeFilter]); 

  // Add debounce for search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchComplaints();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fetchComplaints = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const config = { 
        headers: { Authorization: `Bearer ${user.token}` },
        params: {
          search,
          type: typeFilter,
          startDate: dateRange[0],
          endDate: dateRange[1]
        }
      };
      
      const endpoint = activeTab === 'active' ? '/api/complaints' : '/api/complaints/archived';
      const { data } = await axios.get(endpoint, config);
      setComplaints(data);

      if (activeTab === 'active') {
        const incidents = data.filter(c => c.type === 'incident_report' && c.status === 'pending');
        setIncidentCount(incidents.length);
      }
    } catch (error) {
      console.error(error);
      notifications.show({ title: 'Error', message: 'Failed to fetch tickets', color: 'red' });
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setStatusUpdate(ticket.status);
    setOpenModal(true);
  };

  const handleUpdateStatus = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      
      await axios.put(`/api/complaints/${selectedTicket._id}`, { status: statusUpdate }, config);
      
      notifications.show({ title: 'Updated', message: 'Ticket status updated', color: 'green' });
      setOpenModal(false);
      fetchComplaints(); 
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Update failed', color: 'red' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'in_progress': return 'blue';
      case 'resolved': return 'green';
      case 'rejected': return 'red';
      default: return 'gray';
    }
  };

  const getTypeBadge = (type) => {
    if (type === 'incident_report') return <Badge color="red" variant="filled">Incident</Badge>;
    if (type === 'service_request') return <Badge color="blue" variant="light">Request</Badge>;
    return <Badge color="orange" variant="light">Complaint</Badge>;
  };

  const activeIncidents = complaints.filter(c => c.type === 'incident_report' && c.status === 'pending');

  const rows = complaints.map((ticket) => (
    <Table.Tr key={ticket._id}>
      <Table.Td style={{ fontSize: '0.85rem' }}>
        {/* REVISION 2: Display Ticket ID */}
        <Text fw={700} size="sm">{ticket.ticketId || 'N/A'}</Text>
        <Text span c="dimmed" size="xs">{new Date(ticket.dateFiled).toLocaleDateString()}</Text>
      </Table.Td>
      <Table.Td>
        {ticket.resident ? `${ticket.resident.lastName}, ${ticket.resident.firstName}` : 'Unknown'}
      </Table.Td>
      <Table.Td>{getTypeBadge(ticket.type)}</Table.Td>
      <Table.Td style={{ maxWidth: '200px' }}>
        <Text truncate fw={500}>{ticket.subject}</Text>
      </Table.Td>
      <Table.Td>
        <Badge color={getStatusColor(ticket.status)} variant="dot">{ticket.status.replace('_', ' ').toUpperCase()}</Badge>
      </Table.Td>
      <Table.Td>
        <Button variant="subtle" size="xs" onClick={() => handleViewTicket(ticket)}>
          <IconEye size={18} />
        </Button>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Layout>
      {/* INCIDENT ALERT BANNER */}
      {activeTab === 'active' && incidentCount > 0 && (
        <Alert 
          variant="filled" 
          color="red" 
          title={
            <Group justify="space-between" style={{ width: '100%' }}>
               <Group gap="xs">
                 <IconAlertTriangle size={20} />
                 {incidentCount} Pending Incident Report{incidentCount > 1 ? 's' : ''}
               </Group>
               <Button 
                variant="white" 
                size="xs" 
                color="red" 
                onClick={() => setShowIncidentDetails(!showIncidentDetails)}
                rightSection={<IconChevronDown size={14} style={{ transform: showIncidentDetails ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
               >
                 View Details
               </Button>
            </Group>
          } 
          mb="lg"
          styles={{ title: { width: '100%' } }}
        >
           <Collapse in={showIncidentDetails}>
              <Box mt="sm" style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '4px', padding: '10px' }}>
                 {activeIncidents.map(inc => (
                    <Group key={inc._id} mb="xs" align="flex-start">
                        <IconMessageExclamation size={16} style={{ marginTop: 4 }} />
                        <Box>
                            <Text size="sm" fw={700}>
                                [{inc.ticketId}] {inc.resident ? `${inc.resident.lastName}, ${inc.resident.firstName}` : 'Unknown'} - {inc.subject}
                            </Text>
                            <Text size="xs">{inc.description}</Text>
                            <Text size="xs" mt={4}>
                                {new Date(inc.dateFiled).toLocaleString()}
                            </Text>
                        </Box>
                        <Button size="xs" variant="light" color="red" ml="auto" onClick={() => handleViewTicket(inc)}>
                            Manage
                        </Button>
                    </Group>
                 ))}
              </Box>
           </Collapse>
        </Alert>
      )}

      <Group justify="space-between" mb="lg">
        <Title order={2}>Ticket Management</Title>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab} color="teal" mb="md">
        <Tabs.List>
          <Tabs.Tab value="active" leftSection={<IconMessageExclamation size={16} />}>Active Tickets</Tabs.Tab>
          <Tabs.Tab value="archived" leftSection={<IconArchive size={16} />}>Archived History</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="active">
            <Paper p="md" mb="lg" withBorder shadow="xs">
                <Grid>
                    <Grid.Col span={4}>
                        <TextInput
                            placeholder="Search by ID or Subject..." // Updated placeholder
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            leftSection={<IconSearch size={16} />}
                        />
                    </Grid.Col>
                    <Grid.Col span={3}>
                         <Select 
                            placeholder="Filter Type"
                            value={typeFilter}
                            onChange={setTypeFilter}
                            data={[
                                { value: 'complaint', label: 'Complaint' },
                                { value: 'service_request', label: 'Service Request' },
                                { value: 'incident_report', label: 'Incident Report' }
                            ]}
                            clearable
                            leftSection={<IconFilter size={16} />}
                        />
                    </Grid.Col>
                    <Grid.Col span={5}>
                        <DatePickerInput
                            type="range"
                            placeholder="Filter by Date Range"
                            value={dateRange}
                            onChange={setDateRange}
                            clearable
                        />
                    </Grid.Col>
                </Grid>
            </Paper>
        </Tabs.Panel>
      </Tabs>

      <Paper withBorder shadow="sm">
        <ScrollArea h={500}>
            <Table striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Ticket ID / Date</Table.Th> {/* Updated Header */}
                        <Table.Th>Resident</Table.Th>
                        <Table.Th>Type</Table.Th>
                        <Table.Th>Subject</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Action</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.length > 0 ? rows : (
                        <Table.Tr><Table.Td colSpan={6} align="center">No tickets found.</Table.Td></Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </ScrollArea>
      </Paper>

      {/* MANAGE TICKET MODAL */}
      <Modal opened={openModal} onClose={() => setOpenModal(false)} title="Manage Ticket" size="lg">
        {selectedTicket && (
            <Box>
                <Group justify="space-between" mb="md">
                    <Box>
                        {/* REVISION 2: Display Ticket ID in Modal */}
                        <Text size="sm" c="dimmed">Ticket ID: <Text span fw={700} c="dark">{selectedTicket.ticketId}</Text></Text>
                        <Text fw={700} size="lg">{selectedTicket.subject}</Text>
                    </Box>
                    {getTypeBadge(selectedTicket.type)}
                </Group>

                <Grid mb="md">
                    <Grid.Col span={6}>
                        <Text fw={500} size="sm">Resident:</Text>
                        <Text>{selectedTicket.resident ? `${selectedTicket.resident.lastName}, ${selectedTicket.resident.firstName}` : 'Unknown'}</Text>
                    </Grid.Col>
                    <Grid.Col span={6}>
                        <Text fw={500} size="sm">Date Filed:</Text>
                        <Text>{new Date(selectedTicket.dateFiled).toLocaleString()}</Text>
                    </Grid.Col>
                </Grid>

                <Text fw={500} size="sm" mb="xs">Description:</Text>
                <Paper withBorder p="sm" bg="gray.0" mb="lg">
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.description}</Text>
                </Paper>

                {/* --- ATTACHMENTS SECTION --- */}
                {selectedTicket.proofImages && selectedTicket.proofImages.length > 0 && (
                    <Box mb="lg">
                        <Text fw={500} size="sm" mb="xs">Attachments:</Text>
                        <Group>
                            {selectedTicket.proofImages.map((img, i) => (
                                <Image 
                                    key={i} 
                                    src={img} 
                                    h={100} w={100} radius="md" 
                                    style={{ cursor: 'pointer', border: '1px solid #eee' }}
                                    onClick={() => setPreviewImage(img)}
                                />
                            ))}
                        </Group>
                    </Box>
                )}

                <Select
                    label="Update Status"
                    description="Setting status to 'Resolved' will automatically archive this ticket."
                    value={statusUpdate}
                    onChange={setStatusUpdate}
                    data={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'in_progress', label: 'In Progress' },
                        { value: 'resolved', label: 'Resolved' },
                        { value: 'rejected', label: 'Rejected' },
                    ]}
                    mb="lg"
                />

                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setOpenModal(false)}>Cancel</Button>
                    <Button color="teal" onClick={handleUpdateStatus} leftSection={<IconCheck size={18} />}>
                        Update Status
                    </Button>
                </Group>
            </Box>
        )}
      </Modal>

      {/* FULL SCREEN IMAGE PREVIEW */}
      <Modal 
        opened={!!previewImage} 
        onClose={() => setPreviewImage(null)} 
        size="xl"
        withCloseButton={false}
        styles={{ body: { padding: 0 }, content: { backgroundColor: 'transparent', boxShadow: 'none' } }}
        centered
      >
        <Box pos="relative">
            <ActionIcon 
                onClick={() => setPreviewImage(null)} 
                color="gray"
                variant="filled"
                radius="xl"
                style={{ position: 'absolute', right: 10, top: 10, zIndex: 10 }}
            >
                <IconX size={20} />
            </ActionIcon>
            <Image src={previewImage} radius="md" />
        </Box>
      </Modal>
    </Layout>
  );
};

export default Complaints;